// Org context — the tenant identity every page needs after login.
// Loaded once per session from the caller's JWT claims + the organizations
// row; cached in-memory and in localStorage for offline boot.
//
// Usage (W4 wires this into each page's boot):
//   const org = await loadOrgContext(sbClient);
//   org.id, org.name, org.branding.primary, org.role, org.locale, ...

import { setLocale, apply as applyI18n } from './i18n.js';

let _ctx = null;

const CACHE_PREFIX = 'ordinex_org_';

// The org's locale drives t() and the static [data-i18n] pass (W8). English
// pages stay as authored; other locales rewrite once the context resolves.
function activateLocale(ctx) {
  if (!ctx?.locale) return;
  setLocale(ctx.locale);
  try { applyI18n(); } catch { /* pre-DOM boot */ }
}

function claimsFromSession(session) {
  try {
    const payload = JSON.parse(atob(session.access_token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.app_metadata ?? {};
  } catch {
    return {};
  }
}

function shape(orgRow, role) {
  return {
    id: orgRow.id,
    name: orgRow.name,
    slug: orgRow.slug,
    role,
    locale: orgRow.locale,
    units: orgRow.units,
    currency: orgRow.currency,
    timezone: orgRow.timezone,
    dateFormat: orgRow.date_format,
    features: orgRow.features ?? {},
    jurisdiction: orgRow.jurisdiction ?? 'generic',
    // Per-org regulatory-notice wording (jurisdictions rollout, 2026-07-26).
    // Overrides win over the seeded jurisdiction content; jurisdictionContent
    // is attached separately in loadOrgContext (different table, non-fatal).
    regNoticeOverrides: orgRow.reg_notice_overrides ?? {},
    // Ordered crew role catalog for the Schedule. Fallback covers org rows
    // (and localStorage caches) from before the labor_roles column existed.
    laborRoles: Array.isArray(orgRow.labor_roles) && orgRow.labor_roles.length
      ? orgRow.labor_roles
      : [{ id: 'fm', name: 'Foreman' }, { id: 'bl', name: 'Tradesperson' }, { id: 'hp', name: 'Helper' }],
    // Paid sick leave is TWO numbers and both are the org's: how many days a
    // year are paid, and how long a paid day is. Left hard-coded, a company on
    // 7.5-hour days was quietly paid as if it ran 8s.
    sickDaysPerYear: orgRow.sick_days_per_year,
    paidSickHours: orgRow.paid_sick_hours,
    // Time clock: the org's break and overtime rules, read by js/core/time-clock.js
    // through settingsFrom(). Hard-coded, a company off the BC defaults would be
    // quietly paid by them. Org rows and localStorage caches from before these
    // columns existed leave them undefined; settingsFrom() fills the BC defaults.
    // 0 on an overtime threshold means that rule is off.
    clockBreakMode: orgRow.clock_break_mode,               // 'none' | 'auto' | 'manual'
    clockBreakMinutes: orgRow.clock_break_minutes,         // unpaid minutes taken off a shift (auto)
    clockBreakAfterHours: orgRow.clock_break_after_hours,  // shift length at which the auto break comes off
    otDailyAfter: orgRow.ot_daily_after,                   // hours a day before 1.5x
    otDoubleAfter: orgRow.ot_double_after,                 // hours a day before 2x
    otWeeklyAfter: orgRow.ot_weekly_after,                 // regular hours a week before 1.5x
    clockGpsFlagM: orgRow.clock_gps_flag_m,                // metres from the job site before a punch is flagged
    statHolidays: orgRow.stat_holidays ?? [],              // ['YYYY-MM-DD'] stat holidays the company observes
    isDemo: orgRow.is_demo,
    branding: {
      primary: orgRow.brand_primary,
      secondary: orgRow.brand_secondary,
      logoPath: orgRow.logo_path,
      address: orgRow.company_address,
      phone: orgRow.company_phone,
      email: orgRow.company_email,
    },
  };
}

export async function loadOrgContext(sb) {
  if (_ctx) return _ctx;

  const { data: { session } } = await sb.auth.getSession();
  if (!session) return null;
  const claims = claimsFromSession(session);
  const role = claims.org_role ?? null;
  const cacheKey = CACHE_PREFIX + session.user.id;

  try {
    const { data, error } = await sb.from('organizations').select('*').single();
    if (error) throw error;
    _ctx = shape(data, role);
    // Seeded jurisdiction content (notices + provenance) — non-fatal: a miss
    // leaves jurisdictionContent null and resolveRegNoticeMeta falls back to
    // the hardcoded notices, exactly the pre-rollout behavior. Riding inside
    // _ctx means the localStorage cache below keeps it for offline boots.
    try {
      const { data: jur } = await sb.from('jurisdictions')
        .select('content').eq('key', _ctx.jurisdiction).maybeSingle();
      _ctx.jurisdictionContent = jur?.content ?? null;
    } catch { _ctx.jurisdictionContent = null; }
    try { localStorage.setItem(cacheKey, JSON.stringify(_ctx)); } catch { /* quota */ }
    activateLocale(_ctx);
    return _ctx;
  } catch {
    // offline boot: last known org context for this user
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) { _ctx = JSON.parse(cached); activateLocale(_ctx); return _ctx; }
    } catch { /* corrupt cache */ }
    return null;
  }
}

export function getOrgContext() {
  return _ctx;
}

export function clearOrgContext(userId) {
  _ctx = null;
  if (userId) { try { localStorage.removeItem(CACHE_PREFIX + userId); } catch { /* ignore */ } }
}

// Writes to the organizations row (name/address/phone/email/colors/logo_path
// — see shape() above for the full field map) and reloads the context so
// every [data-org-*] element and window.OrdinexBrand pick up the change
// immediately, without a page refresh. `updates` uses DB column names
// (brand_primary, logo_path, ...), not the camelCase branding.* shape.
export async function saveOrgBranding(sb, updates) {
  if (!_ctx) throw new Error('saveOrgBranding: org context not loaded');
  const { error } = await sb.from('organizations').update(updates).eq('id', _ctx.id);
  if (error) throw error;
  const { data: { session } } = await sb.auth.getSession();
  clearOrgContext(session?.user?.id);
  return loadOrgContext(sb);
}
